/**
 * Central error reporting service for DiceCapture web-app.
 * Provides unified error handling with toast notifications and persistent logging.
 * @module errorReporter
 */

import { toast } from 'sonner';
import { useErrorLogStore } from '@/stores/errorLogStore';
import type {
  ReportErrorOptions,
  ErrorSeverity,
  ErrorSource,
  ErrorLogEntry
} from '@/types/errorLog';
import { ERROR_PREFIXES, FRIENDLY_SOURCE_NAMES } from '@/types/errorLog';

const SERVICE_TAG = '[ERROR_REPORTER]';

/**
 * Format error message for user-facing display
 */
function formatErrorMessage(
  severity: ErrorSeverity,
  source: ErrorSource,
  message: string
): string {
  const prefix = ERROR_PREFIXES[severity];
  const sourceName = FRIENDLY_SOURCE_NAMES[source];
  return `${prefix}: ${sourceName} - ${message}`;
}

/**
 * Get default toast duration based on severity
 */
function getDefaultDuration(severity: ErrorSeverity): number {
  return severity === 'info' ? 3000 : 5000;
}

/**
 * Report an error to the error log and optionally show a toast.
 *
 * @param options - Error reporting options
 * @returns The error log entry ID
 *
 * @example
 * // Basic usage
 * reportError({
 *   source: 'CAMERA',
 *   userMessage: 'Unable to access camera',
 *   originalError: error
 * });
 *
 * @example
 * // With context and custom severity
 * reportError({
 *   source: 'WEBSOCKET',
 *   userMessage: 'Connection lost',
 *   severity: 'warning',
 *   context: { sessionCode: 'ABC123', attempt: 3 }
 * });
 */
export function reportError(options: ReportErrorOptions): string {
  const {
    source,
    userMessage,
    technicalDetails,
    originalError,
    context,
    severity = 'error',
    showToast = true,
    toastDuration
  } = options;

  // Extract error message from Error object
  const errorMessage =
    originalError instanceof Error ? originalError.message : originalError;

  // Log to console (preserves existing console.error behavior)
  const consoleMethod = severity === 'error' ? console.error : console.warn;
  consoleMethod(`[${source}] ${userMessage}`, {
    error: errorMessage,
    technicalDetails,
    context
  });

  // Add to error log store
  const addEntry = useErrorLogStore.getState().addEntry;
  const entry: Omit<ErrorLogEntry, 'id' | 'timestamp' | 'acknowledged'> = {
    severity,
    source,
    userMessage,
    technicalDetails,
    originalError: errorMessage,
    context
  };
  const entryId = addEntry(entry);

  // Show toast notification
  if (showToast) {
    const formattedMessage = formatErrorMessage(severity, source, userMessage);
    const duration = toastDuration ?? getDefaultDuration(severity);

    const toastOptions = {
      duration,
      description: technicalDetails
    };

    switch (severity) {
      case 'error':
        toast.error(formattedMessage, toastOptions);
        break;
      case 'warning':
        toast.warning(formattedMessage, toastOptions);
        break;
      case 'info':
        toast.info(formattedMessage, toastOptions);
        break;
    }

    console.info(`${SERVICE_TAG} Toast shown`, { severity, entryId });
  }

  return entryId;
}

/**
 * Report a warning (convenience wrapper)
 *
 * @param options - Error reporting options (severity is set to 'warning')
 * @returns The error log entry ID
 */
export function reportWarning(options: Omit<ReportErrorOptions, 'severity'>): string {
  return reportError({ ...options, severity: 'warning' });
}

/**
 * Report info (convenience wrapper)
 *
 * @param options - Error reporting options (severity is set to 'info')
 * @returns The error log entry ID
 */
export function reportInfo(options: Omit<ReportErrorOptions, 'severity'>): string {
  return reportError({ ...options, severity: 'info' });
}

/**
 * Scoped reporter interface for use within services
 */
interface ScopedReporter {
  error: (userMessage: string, options?: Partial<Omit<ReportErrorOptions, 'source'>>) => string;
  warning: (userMessage: string, options?: Partial<Omit<ReportErrorOptions, 'source'>>) => string;
  info: (userMessage: string, options?: Partial<Omit<ReportErrorOptions, 'source'>>) => string;
}

/**
 * Create a scoped error reporter for a specific service.
 * Recommended for use within service files.
 *
 * @param source - The error source (maps to SERVICE_TAG)
 * @returns A scoped reporter with error, warning, and info methods
 *
 * @example
 * const reporter = createScopedReporter('CAMERA');
 * reporter.error('Failed to initialize', { context: { deviceId } });
 * reporter.warning('Low light detected', { showToast: false });
 */
export function createScopedReporter(source: ErrorSource): ScopedReporter {
  return {
    error: (userMessage, options) =>
      reportError({ source, userMessage, ...options }),
    warning: (userMessage, options) =>
      reportWarning({ source, userMessage, ...options }),
    info: (userMessage, options) =>
      reportInfo({ source, userMessage, ...options })
  };
}
